import {useState, useEffect, useCallback, useMemo} from "react";
import secureStorage from "../../../utils/secureStorage";
import {ALL_KPI_IDS, ALL_CHART_IDS} from "./widgetCatalog";

const STORAGE_PREFIX = "activity_kpi_prefs_";

const DEFAULT_PREFS = {
    // Hidden by default — the user opts in via the selector panel.
    visibleKpis: [],
    visibleCharts: [],
};

const sanitize = (prefs) => {
    const safe = prefs && typeof prefs === "object" ? prefs : {};
    return {
        // Keep only ids that still exist in the catalog, preserving stored order.
        visibleKpis: Array.isArray(safe.visibleKpis)
            ? safe.visibleKpis.filter((id) => ALL_KPI_IDS.includes(id))
            : [],
        visibleCharts: Array.isArray(safe.visibleCharts)
            ? safe.visibleCharts.filter((id) => ALL_CHART_IDS.includes(id))
            : [],
    };
};

/**
 * Persists which KPI cards / charts are visible (and the KPI card order) on a
 * per-account basis in localStorage. Defaults to everything hidden.
 */
const useKpiPreferences = () => {
    const [accountId, setAccountId] = useState(null);
    const [prefs, setPrefs] = useState(DEFAULT_PREFS);
    const [ready, setReady] = useState(false);

    // Resolve the selected account, then hydrate its stored preferences.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const account = await secureStorage.getItem("selectedAccount");
            if (cancelled) return;
            const id = account?.id || "default";
            setAccountId(id);
            try {
                const raw = localStorage.getItem(`${STORAGE_PREFIX}${id}`);
                setPrefs(raw ? sanitize(JSON.parse(raw)) : DEFAULT_PREFS);
            } catch {
                setPrefs(DEFAULT_PREFS);
            }
            setReady(true);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Persist on every change once we know the account.
    useEffect(() => {
        if (!ready || !accountId) return;
        try {
            localStorage.setItem(`${STORAGE_PREFIX}${accountId}`, JSON.stringify(prefs));
        } catch {
            /* storage unavailable — non-fatal */
        }
    }, [prefs, accountId, ready]);

    const toggleKpi = useCallback((id) => {
        setPrefs((prev) => {
            const isVisible = prev.visibleKpis.includes(id);
            return {
                ...prev,
                visibleKpis: isVisible
                    ? prev.visibleKpis.filter((k) => k !== id)
                    : [...prev.visibleKpis, id],
            };
        });
    }, []);

    const toggleChart = useCallback((id) => {
        setPrefs((prev) => {
            const isVisible = prev.visibleCharts.includes(id);
            return {
                ...prev,
                visibleCharts: isVisible
                    ? prev.visibleCharts.filter((c) => c !== id)
                    : [...prev.visibleCharts, id],
            };
        });
    }, []);

    // Move a KPI from one index to another (drag reorder).
    const reorderKpis = useCallback((fromIndex, toIndex) => {
        setPrefs((prev) => {
            const next = [...prev.visibleKpis];
            if (
                fromIndex < 0 ||
                toIndex < 0 ||
                fromIndex >= next.length ||
                toIndex >= next.length
            ) {
                return prev;
            }
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return {...prev, visibleKpis: next};
        });
    }, []);

    const resetAll = useCallback(() => setPrefs(DEFAULT_PREFS), []);

    const isKpiVisible = useCallback((id) => prefs.visibleKpis.includes(id), [prefs]);
    const isChartVisible = useCallback(
        (id) => prefs.visibleCharts.includes(id),
        [prefs]
    );

    return useMemo(
        () => ({
            ready,
            accountId,
            visibleKpis: prefs.visibleKpis,
            visibleCharts: prefs.visibleCharts,
            isKpiVisible,
            isChartVisible,
            toggleKpi,
            toggleChart,
            reorderKpis,
            resetAll,
        }),
        [
            ready,
            accountId,
            prefs,
            isKpiVisible,
            isChartVisible,
            toggleKpi,
            toggleChart,
            reorderKpis,
            resetAll,
        ]
    );
};

export default useKpiPreferences;
